import React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import isEmail from 'validator/lib/isEmail';
import isURL from 'validator/lib/isURL';

import Button from '@components/Button';
import { Dropdown } from '@components/Dropdown';
import Input from '@components/Input';
import Toggle from '@components/Toggle';

import { editAgencySettings } from '@actions/agency';
import { fetchLcfs } from '@actions/locationCustomField';
import { fetchRolesWithAgencyId } from '@actions/role';

import {
  CRM_SMS_DISABLED_VALIDATION_MESSAGE,
  CRM_TEXT_NUMBER_INVALID_VALIDATION_MESSAGE,
  isValidAgencyTextNumber,
} from '@utils/agencySmsValidationUtil';
import CyberdyneValidator from '@utils/cyberdyneValidator';
import ErrorMessageGenerator, { FieldValidationType } from '@utils/ErrorMessageGenerator';
import { stateOptions } from '@utils/StateUtil';

import DefaultCityModal from '../../DefaultCityModal';
import FaqModal from '../../FaqModal';
import GISDirectLinkModal from '../../GISDirectLinkModal';

import './style.scss';

export interface IAgencySettingsProps {
  agency: IAgencySettings;
  lcfs: ILcfResponse[];
  roles: IRole[];
  fetchLcfs: TFetchLcfs;
  fetchRoles: TFetchRolesWithAgencyId;
  editAgencySettings: TEditAgencySettings;
}

export interface IAgencySettingsState {
  agency: Partial<IAgencySettings>;
  stateSearchQuery: string;
  error: {
    name: IFieldValidation;
    agencyTimezone: IFieldValidation;
    websiteURL: IFieldValidation;
    whitelistURL: IFieldValidation;
    email: IFieldValidation;
    streetAddress: IFieldValidation;
    city: IFieldValidation;
    state: IFieldValidation;
    zip: IFieldValidation;
    sendFromEmail: IFieldValidation;
    sendFromPhoneNumber: IFieldValidation;
  };
  isGISDirectLinkModalOpen: boolean;
  isDefaultCityModalOpen: boolean;
  isFaqModalOpen: boolean;
  lcfIds: number[];
  lcfOptions: IPickListOptions[];
  roleOptions: IPickListOptions[];

  roleId: number;
}

const cyberdyneValidator = new CyberdyneValidator();
const errorMsgGenerator = new ErrorMessageGenerator();
const AGENCY_EMAIL_SUFFIX = '@customerportal.help';

export class AgencySettings extends React.Component<IAgencySettingsProps, IAgencySettingsState> {
  readonly timezoneOptions = [TimeZone.HST, TimeZone.AKST, TimeZone.PST, TimeZone.CST, TimeZone.EST, TimeZone.MST];

  constructor(props: IAgencySettingsProps) {
    super(props);
    this.state = this.getEmptyState();
  }

  componentDidMount() {
    const { agency } = this.props;
    const sendFromEmail = agency.sendFromEmail ? agency.sendFromEmail.split('@')[0] : '';

    this.setState({
      agency: {
        ...agency,
        sendFromEmail,
      },
    });

    this.props.fetchLcfs(agency.id);
    this.props.fetchRoles(agency.id);
  }

  componentDidUpdate(prevProps: Readonly<IAgencySettingsProps>): void {
    if (this.props.agency.crmIsActive !== prevProps.agency.crmIsActive) {
      this.setState((prevState) => ({
        agency: {
          ...prevState.agency,
          crmIsActive: this.props.agency.crmIsActive,
        },
      }));
    }

    if (this.props.lcfs && this.props.lcfs !== prevProps.lcfs) {
      const lcfOptions: IPickListOptions[] = [
        {
          value: -1,
          label: 'Select custom location field',
        },
      ];

      this.props.lcfs.forEach((lcf, index) => {
        lcfOptions.push({
          value: lcf.id,
          label: lcf.isActive ? lcf.label : `*${lcf.label}`,
          isDisabled: index === 0 ? false : this.state.lcfIds.includes(lcf.id),
        });
      });

      this.setState({ lcfOptions });
    }

    if (this.props.roles?.length && this.props.roles !== prevProps.roles) {
      const { defaultRoleId } = this.props.agency;

      const roleOptions: IPickListOptions[] = [
        {
          value: null,
          label: 'Select Default User role',
        },
      ];

      this.props.roles
        .filter((role) => role.isActive)
        .forEach((role, index) => {
          roleOptions.push({
            value: role.id,
            label: role.label,
            isDisabled: index === 0 ? false : defaultRoleId === role.id,
          });
        });

      this.setState({ roleOptions, roleId: defaultRoleId });
    }
  }

  private getUpdatedRoleOptions(roleId: number): IPickListOptions[] {
    const roleOptions = [...this.state.roleOptions];
    roleOptions.forEach((role, index) => {
      role.isDisabled = index === 0 ? false : roleId === parseInt(role.value, 10);
    });

    return roleOptions;
  }

  getEmptyState = (): IAgencySettingsState => {
    const errorInitState = {
      errorState: null,
      errorMessage: '',
    };

    return {
      agency: {
        name: null,
        websiteURL: null,
        whitelistURL: null,
        streetAddress: null,
        agencyTimezone: null,
        email: null,
        city: null,
        state: null,
        zip: null,
        defaultCity: null,
        gisSetting: GISSetting.ENTERPRISE,
        gisVersion: GISVersion.GIS_1_0,
        gisDirectURL: null,
        permissionPreference: PermissionPreference.GROUPS,
        SSOProvider: SSOProvider.OKTA,
        sendFromEmail: null,
        isSMSEnabled: false,
        sendFromPhoneNumber: null,
        configQsLcfs: [],
      },
      stateSearchQuery: '',
      error: {
        name: Object.assign({}, errorInitState),
        websiteURL: Object.assign({}, errorInitState),
        whitelistURL: Object.assign({}, errorInitState),
        streetAddress: Object.assign({}, errorInitState),
        agencyTimezone: Object.assign({}, errorInitState),
        email: Object.assign({}, errorInitState),
        city: Object.assign({}, errorInitState),
        state: Object.assign({}, errorInitState),
        zip: Object.assign({}, errorInitState),
        sendFromEmail: Object.assign({}, errorInitState),
        sendFromPhoneNumber: Object.assign({}, errorInitState),
      },
      isGISDirectLinkModalOpen: false,
      isDefaultCityModalOpen: false,
      lcfIds: [-1, -1, -1],
      lcfOptions: [],
      isFaqModalOpen: false,
      roleId: -1,
      roleOptions: [],
    };
  };

  handleChange = (event: any) => {
    this.setState((prevState) => ({
      ...prevState,
      agency: {
        ...prevState.agency,
        [event.target.name]: event.target.value,
      },
    }));
  };

  onStateSearchQueryChange = (params: ILoadMoreParams) => {
    const { query } = params;

    this.setState({ stateSearchQuery: query });
  };

  toggleGISDirectLinkModal = (e?: React.SyntheticEvent<any>) => {
    this.setState((prevState) => ({
      isGISDirectLinkModalOpen: !prevState.isGISDirectLinkModalOpen,
    }));
  };

  toggleDefaultCityModal = (e?: React.SyntheticEvent<any>) => {
    this.setState((prevState) => ({
      isDefaultCityModalOpen: !prevState.isDefaultCityModalOpen,
    }));
  };

  onGISSettingsChange = (e?: React.SyntheticEvent<any>) => {
    const value = e.currentTarget.value;
    if (value !== this.state.agency.gisSetting) {
      if (value === GISSetting.DIRECT) {
        this.toggleGISDirectLinkModal();
      }
      this.setState((prevState) => ({
        ...prevState,
        agency: {
          ...prevState.agency,
          gisSetting: value,
          ...([GISSetting.DIRECT, GISSetting.ENTERPRISE].includes(value) ? { gisVersion: GISVersion.GIS_1_0 } : {}),
        },
      }));
    }
  };

  onSubmitDefaultCity = (defaultCity: string) => (e?: React.SyntheticEvent<HTMLButtonElement>) => {
    this.setState((prevState) => ({
      ...prevState,
      agency: {
        ...prevState.agency,
        defaultCity,
      },
      isDefaultCityModalOpen: false,
    }));
  };

  onSubmitGISDirectLink = (gisDirectURL: string) => (e?: React.SyntheticEvent<HTMLButtonElement>) => {
    this.setState((prevState) => ({
      ...prevState,
      agency: {
        ...prevState.agency,
        gisDirectURL,
        gisSetting: GISSetting.DIRECT,
      },
      isGISDirectLinkModalOpen: false,
    }));
  };

  onDefaultCityToggleChange = (e?: React.SyntheticEvent<any>) => {
    if (!this.state.agency.defaultCity) {
      this.toggleDefaultCityModal(e);
    } else {
      this.setState((prevState) => ({
        ...prevState,
        agency: {
          ...prevState.agency,
          defaultCity: null,
        },
      }));
    }
  };

  onGISActivationToggleChange = (e?: React.SyntheticEvent<any>) => {
    let gisVersion = this.state.agency.gisVersion;

    if (gisVersion === GISVersion.GIS_1_0) {
      gisVersion = GISVersion.GIS_2_0;
    } else if (gisVersion === GISVersion.GIS_2_0) {
      gisVersion = GISVersion.GIS_1_0;
    }

    this.setState((prevState) => ({
      ...prevState,
      agency: {
        ...prevState.agency,
        gisVersion,
      },
    }));
  };

  onGroupPermissionsChange = (e?: React.SyntheticEvent<any>) => {
    const value = e.currentTarget.value;
    this.setState((prevState) => ({
      ...prevState,
      agency: {
        ...prevState.agency,
        permissionPreference: value,
      },
    }));
  };

  onSSOProviderChange = (e?: React.SyntheticEvent<any>) => {
    const value = e.currentTarget.value;

    this.setState((prevState) => ({
      ...prevState,
      agency: {
        ...prevState.agency,
        SSOProvider: value,
      },
    }));
  };

  onSendFromMessageToggle = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const checked = e.currentTarget.checked;
    this.setState((prevState) => ({
      ...prevState,
      agency: {
        ...prevState.agency,
        isSMSEnabled: !!checked,
      },
    }));
  };

  private getUpdatedLcfOptions(lcfIds: number[]): IPickListOptions[] {
    const lcfOptions = [...this.state.lcfOptions];
    lcfOptions.forEach(
      (lcf, index) => (lcf.isDisabled = index === 0 ? false : lcfIds.includes(parseInt(lcf.value, 10)))
    );

    return lcfOptions;
  }

  handleLcfChange = (index: number) => (event: React.SyntheticEvent<HTMLInputElement>) => {
    const newLcfId = parseInt(event.currentTarget.value, 10);

    const lcfIds = [...this.state.lcfIds];
    lcfIds[index] = newLcfId;

    const lcfOptions = this.getUpdatedLcfOptions(lcfIds);

    this.setState((prevState) => ({
      ...prevState,
      lcfIds,
      lcfOptions,
    }));
  };

  onActionCenterAccessChange = (e?: React.SyntheticEvent<any>) => {
    const value = e.currentTarget.value;
    this.setState((prevState) => ({
      ...prevState,
      agency: {
        ...prevState.agency,
        hasActionCenterAccess: value,
      },
    }));
  };

  toggleFaqModal = (e?: React.SyntheticEvent<any>) => {
    this.setState((prevState) => ({
      isFaqModalOpen: !prevState.isFaqModalOpen,
    }));
  };

  onFaqChange = (e?: React.SyntheticEvent<any>) => {
    const value = e.currentTarget.value;
    if (value) {
      this.toggleFaqModal();
    } else {
      this.setState((prevState) => ({
        ...prevState,
        agency: {
          ...prevState.agency,
          isFaqEnabled: value,
        },
      }));
    }
  };

  onSubmitFaq = (elevioAccountId: string) => (e?: React.SyntheticEvent<HTMLButtonElement>) => {
    this.setState((prevState) => ({
      ...prevState,
      agency: {
        ...prevState.agency,
        elevioAccountId,
        isFaqEnabled: true,
      },
      isFaqModalOpen: false,
    }));
  };

  onRoleChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const roleId: number = parseInt(e.currentTarget.value, 10);
    const roleOptions = this.getUpdatedRoleOptions(roleId);

    this.setState((prevState) => ({
      ...prevState,
      roleId,
      roleOptions,
    }));
  };

  validateForm = () => {
    let isValid = true;
    const { agency } = this.state;
    const error = this.getEmptyState().error;
    const crmIsActive = Boolean(agency.crmIsActive);

    if (!agency.name) {
      error.name.errorState = IValidationState.ERROR;
      error.name.errorMessage = 'Agency name cannot be empty';

      isValid = false;
    } else if (agency.name.length > 50) {
      error.name.errorState = IValidationState.ERROR;
      error.name.errorMessage = 'Agency name cannot be longer than 50 characters';

      isValid = false;
    }

    if (!agency.agencyTimezone) {
      error.agencyTimezone.errorState = IValidationState.ERROR;
      error.agencyTimezone.errorMessage = 'Timezone cannot be left empty';

      isValid = false;
    }

    if (!agency.streetAddress) {
      error.streetAddress.errorState = IValidationState.ERROR;
      error.streetAddress.errorMessage = 'Address cannot be empty';

      isValid = false;
    } else if (agency.streetAddress.length > 500) {
      error.streetAddress.errorState = IValidationState.ERROR;
      error.streetAddress.errorMessage = 'Address cannot be longer than 500 characters';

      isValid = false;
    }

    if (!agency.state) {
      error.state.errorState = IValidationState.ERROR;
      error.state.errorMessage = 'State cannot be empty';

      isValid = false;
    } else if (!cyberdyneValidator.isAlphaWithSpaces(agency.state)) {
      error.state.errorState = IValidationState.ERROR;
      error.state.errorMessage = 'Invalid Input';

      isValid = false;
    }

    if (!agency.city) {
      error.city.errorState = IValidationState.ERROR;
      error.city.errorMessage = 'City cannot be empty';

      isValid = false;
    } else if (!cyberdyneValidator.isAlphaWithSpaces(agency.city)) {
      error.city.errorState = IValidationState.ERROR;
      error.city.errorMessage = 'City name cannot have numeric or special characters.';

      isValid = false;
    }

    if (cyberdyneValidator.isEmptyString(agency.zip)) {
      error.zip.errorState = IValidationState.ERROR;
      error.zip.errorMessage = errorMsgGenerator.getMandatoryFieldMessage('Zip');

      isValid = false;
    } else if (cyberdyneValidator.isStringGreaterThanLength(agency.zip, 15)) {
      error.zip.errorState = IValidationState.ERROR;
      error.zip.errorMessage = errorMsgGenerator.getCharacterLimitMessage('Zip', 15);

      isValid = false;
    } else if (agency.zip && !cyberdyneValidator.isZipCode(agency.zip)) {
      error.zip.errorState = IValidationState.ERROR;
      error.zip.errorMessage = errorMsgGenerator.getInvalidFieldMessage('Zip', FieldValidationType.ZIP);

      isValid = false;
    }

    if (cyberdyneValidator.isEmptyString(agency.email)) {
      error.email.errorState = IValidationState.ERROR;
      error.email.errorMessage = errorMsgGenerator.getMandatoryFieldMessage('Email');

      isValid = false;
    } else if (agency.email && agency.email.length > EMAIL_CONFIG.LENGTH) {
      error.email.errorState = IValidationState.ERROR;
      error.email.errorMessage = errorMsgGenerator.getCharacterLimitMessage('Email', EMAIL_CONFIG.LENGTH);

      isValid = false;
    } else if (!isEmail(agency.email)) {
      error.email.errorState = IValidationState.ERROR;
      error.email.errorMessage = errorMsgGenerator.getInvalidFieldMessage('Email', FieldValidationType.EMAIL);

      isValid = false;
    }

    if (!agency.websiteURL) {
      error.websiteURL.errorState = IValidationState.ERROR;
      error.websiteURL.errorMessage = 'Website URL cannot be empty';

      isValid = false;
    } else if (!isURL(agency.websiteURL)) {
      error.websiteURL.errorState = IValidationState.ERROR;
      error.websiteURL.errorMessage = 'Enter valid Website URL.';

      isValid = false;
    } else if (agency.websiteURL.length > 256) {
      error.websiteURL.errorState = IValidationState.ERROR;
      error.websiteURL.errorMessage = 'Website URL cannot be longer than 256 characters';

      isValid = false;
    }

    if (agency.whitelistURL && !isURL(agency.whitelistURL)) {
      error.whitelistURL.errorState = IValidationState.ERROR;
      error.whitelistURL.errorMessage = 'Enter a valid Whitelist URL';

      isValid = false;
    } else if (agency.whitelistURL && agency.whitelistURL.length > 256) {
      error.whitelistURL.errorState = IValidationState.ERROR;
      error.whitelistURL.errorMessage = 'Whitelist URL cannot be longer than 256 characters';

      isValid = false;
    }

    if (cyberdyneValidator.isEmptyValue(agency.sendFromEmail)) {
      error.sendFromEmail = {
        errorState: IValidationState.ERROR,
        errorMessage: errorMsgGenerator.getMandatoryFieldMessage('Email'),
      };

      isValid = false;
    } else if (
      cyberdyneValidator.isStringGreaterThanLength(
        `${agency.sendFromEmail}${AGENCY_EMAIL_SUFFIX}`.trim(),
        EMAIL_CONFIG.LENGTH
      )
    ) {
      error.sendFromEmail = {
        errorState: IValidationState.ERROR,
        errorMessage: errorMsgGenerator.getCharacterLimitMessage('Email', EMAIL_CONFIG.LENGTH),
      };

      isValid = false;
    } else if (!cyberdyneValidator.isEmail(`${agency.sendFromEmail}${AGENCY_EMAIL_SUFFIX}`)) {
      error.sendFromEmail = {
        errorState: IValidationState.ERROR,
        errorMessage: errorMsgGenerator.getInvalidFieldMessage('Email', FieldValidationType.EMAIL),
      };
      isValid = false;
    }
    if (crmIsActive && !agency.isSMSEnabled) {
      error.sendFromPhoneNumber = {
        errorState: IValidationState.ERROR,
        errorMessage: CRM_SMS_DISABLED_VALIDATION_MESSAGE,
      };
      isValid = false;
    } else if (crmIsActive && !isValidAgencyTextNumber(agency.sendFromPhoneNumber)) {
      error.sendFromPhoneNumber = {
        errorState: IValidationState.ERROR,
        errorMessage: CRM_TEXT_NUMBER_INVALID_VALIDATION_MESSAGE,
      };
      isValid = false;
    } else if (!crmIsActive && agency.isSMSEnabled) {
      if (cyberdyneValidator.isEmptyValue(agency.sendFromPhoneNumber)) {
        error.sendFromPhoneNumber = {
          errorState: IValidationState.ERROR,
          errorMessage: errorMsgGenerator.getMandatoryFieldMessage('Send From Text Message'),
        };
        isValid = false;
      } else if (!cyberdyneValidator.isPhone(agency.sendFromPhoneNumber)) {
        error.sendFromPhoneNumber = {
          errorState: IValidationState.ERROR,
          errorMessage: errorMsgGenerator.getInvalidFieldMessage('Send From Text Message', FieldValidationType.PHONE),
        };
        isValid = false;
      } else if (cyberdyneValidator.isStringGreaterThanLength(agency.sendFromPhoneNumber, 15)) {
        error.sendFromPhoneNumber = {
          errorState: IValidationState.ERROR,
          errorMessage: errorMsgGenerator.getCharacterLimitMessage('Send From Text Message', 15),
        };
        isValid = false;
      } else if (cyberdyneValidator.isStringLessThanLength(agency.sendFromPhoneNumber, 10)) {
        error.sendFromPhoneNumber = {
          errorState: IValidationState.ERROR,
          errorMessage: errorMsgGenerator.getLeastCharacterMessage('Send From Text Message', 10),
        };
        isValid = false;
      }
    }

    this.setState({ error });

    return isValid;
  };

  getRequestFromState = (): IAgencySettingEditRequest => {
    const {
      agencyTimezone,
      city,
      email,
      name,
      state,
      zip,
      streetAddress,
      websiteURL,
      whitelistURL,
      defaultCity,
      gisSetting,
      gisVersion,
      gisDirectURL,
      permissionPreference,
      hasActionCenterAccess,
      SSOProvider,
      isSMSEnabled,
      isFaqEnabled,
    } = this.state.agency;

    const sendFromEmail = `${this.state.agency.sendFromEmail}${AGENCY_EMAIL_SUFFIX}`;
    const sendFromPhoneNumber = this.state.agency.isSMSEnabled ? this.state.agency.sendFromPhoneNumber : null;
    const elevioAccountId = this.state.agency.isFaqEnabled ? this.state.agency.elevioAccountId : null;

    const { lcfIds, roleId } = this.state;

    const agencyRequest: IAgencySettingEditRequest = {
      agencyTimezone,
      city,
      name,
      state,
      zip,
      streetAddress,
      websiteURL,
      whitelistURL,
      defaultCity,
      gisSetting,
      gisVersion,
      permissionPreference,
      gisDirectURL,
      SSOProvider,
      sendFromEmail,
      isSMSEnabled,
      sendFromPhoneNumber,
      hasActionCenterAccess,
      isFaqEnabled,
      elevioAccountId,
      lcfIds,
      defaultRoleId: roleId,
    };

    if (email) {
      agencyRequest.email = email;
    }
    if (whitelistURL !== null) {
      agencyRequest.whitelistURL = whitelistURL;
    }

    return agencyRequest;
  };

  onSubmit = () => {
    if (this.validateForm()) {
      const { id: agencyId } = this.props.agency;
      const agencyRequest = this.getRequestFromState();

      this.props.editAgencySettings(agencyId, agencyRequest);
    }
  };

  render() {
    const { agency, stateSearchQuery, lcfOptions, lcfIds, roleOptions, roleId, error } = this.state;
    const gisSettingsOptions = [
      { value: GISSetting.ENTERPRISE, label: 'GIS Enterprise: Hosted' },
      { value: GISSetting.LITE, label: 'GIS Lite' },
      { value: GISSetting.DIRECT, label: 'GIS Enterprise: Direct' },
    ];

    const groupPermissions = [
      { value: 'GROUPS', label: 'ON' },
      { value: 'USERS', label: 'OFF' },
    ];

    const actionCenterAccessOptions = [
      { value: true, label: 'ON' },
      { value: false, label: 'OFF' },
    ];

    const ssoSettingsOptions = [
      { value: SSOProvider.AD, label: 'Active Directory/SSO' },
      { value: SSOProvider.OKTA, label: 'Authentication by Okta' },
    ];

    const faqOptions = [
      { value: true, label: 'ON' },
      { value: false, label: 'OFF' },
    ];

    return (
      <div className="agency-setting">
        <div className="agency-form__info">
          <h2>Agency information</h2>
          <div className="agency-setting__info-fields">
            <div className="agency-setting__info-fields--first">
              <Input
                field={{
                  label: 'Agency Name',
                  type: CustomFieldType.TEXT,
                  isRequired: true,
                  value: agency.name,
                }}
                width={3}
                name="name"
                onChange={this.handleChange}
                error={error.name}
              />
              <Input
                field={{
                  label: 'Street Address',
                  type: CustomFieldType.TEXT,
                  isRequired: true,
                  value: agency.streetAddress,
                }}
                width={3}
                name="streetAddress"
                onChange={this.handleChange}
                error={error.streetAddress}
              />
              <Input
                field={{
                  label: 'City',
                  type: CustomFieldType.TEXT,
                  isRequired: true,
                  value: agency.city,
                }}
                width={8}
                name="city"
                className="info-city-state"
                onChange={this.handleChange}
                error={error.city}
              />
              <Dropdown
                name="state"
                id="state"
                label="State"
                value={agency.state}
                options={stateOptions(stateSearchQuery)}
                onChange={this.handleChange}
                searchMode={true}
                searchPlaceholder="Search state..."
                error={error.state}
                required={true}
                type={DropdownType.BORDER}
                className={`field-section info-city-state ${agency.state ? 'option-selected' : ''}`}
                shouldHighlightMatchingSubstring={false}
                onLoadMoreData={this.onStateSearchQueryChange}
              />
              <Input
                field={{
                  label: 'Zip',
                  type: CustomFieldType.TEXT,
                  isRequired: true,
                  value: agency.zip,
                }}
                width={3}
                name="zip"
                onChange={this.handleChange}
                error={error.zip}
              />
            </div>
            <div className="agency-setting__info-fields--rest">
              <Input
                field={{
                  label: 'Timezone',
                  type: CustomFieldType.PICKLIST,
                  isRequired: true,
                  value: agency.agencyTimezone,
                  options: this.timezoneOptions,
                }}
                width={3}
                name="agencyTimezone"
                error={error.agencyTimezone}
                onChange={this.handleChange}
              />
              <Input
                field={{
                  label: 'Email',
                  type: CustomFieldType.TEXT,
                  isRequired: true,
                  value: agency.email,
                }}
                width={3}
                name="email"
                onChange={this.handleChange}
                error={error.email}
              />
              <Input
                field={{
                  label: 'Website URL',
                  type: CustomFieldType.TEXT,
                  isRequired: true,
                  value: agency.websiteURL,
                }}
                width={3}
                name="websiteURL"
                onChange={this.handleChange}
                error={error.websiteURL}
              />
              <Input
                field={{
                  label: 'Whitelisted URL',
                  type: CustomFieldType.TEXT,
                  isRequired: false,
                  value: agency.whitelistURL,
                }}
                width={3}
                name="whitelistURL"
                onChange={this.handleChange}
                error={error.whitelistURL}
              />
            </div>
          </div>
        </div>
        <span className="agency-setting__body-br" />
        <div>
          <h2>Agency Settings</h2>
          <div className={classnames('settings-parameters')}>
            <div className={classnames('grid-3-cols-2-rows')}>
              <Input
                field={{
                  label: 'GIS Settings',
                  type: OtherFieldType.RADIOBUTTON,
                  isRequired: false,
                  value: this.state.agency.gisSetting,
                }}
                width={12}
                valueLabelOptions={gisSettingsOptions}
                name="gisSetting"
                onChange={this.onGISSettingsChange}
              />
              <div
                className={classnames(
                  agency.gisSetting === GISSetting.DIRECT
                    ? 'default-city-gis-direct-section'
                    : 'default-city-non-gis-direct-section'
                )}
              >
                <div className={classnames('default-city-section')}>
                  <Toggle
                    bsStyle="primary"
                    onToggle={this.onDefaultCityToggleChange}
                    toggleState={
                      // To show active toggle when modal open
                      this.state.isDefaultCityModalOpen || !!this.state.agency.defaultCity
                    }
                  >
                    Default City
                  </Toggle>
                  {!!this.state.agency.defaultCity && (
                    <>
                      <span className={classnames('default-city-name')}>{this.state.agency.defaultCity}</span>
                      <span className={classnames('separator')}>|</span>
                      <a onClick={this.toggleDefaultCityModal}>Change</a>
                    </>
                  )}
                </div>
                {agency.gisSetting === GISSetting.DIRECT && !!agency.gisDirectURL && (
                  <div className={classnames('gis-direct-url', { hide: agency.gisVersion === GISVersion.GIS_2_0 })}>
                    {'URL '}
                    <span className={classnames('gis-esri-url')}>{agency.gisDirectURL}</span>
                    <span className={classnames('separator')}>|</span>
                    <a onClick={this.toggleGISDirectLinkModal}>Change</a>
                  </div>
                )}
              </div>
              {(agency.gisSetting === GISSetting.ENTERPRISE ||
                (agency.gisSetting === GISSetting.DIRECT && !!agency.gisDirectURL)) && (
                <div className={classnames('gis-activation-section')}>
                  <span
                    className={classnames(
                      agency.gisVersion === GISVersion.GIS_1_0
                        ? 'gis-activation-section-label-before-active'
                        : 'gis-activation-section-label-before'
                    )}
                  >
                    {agency.gisSetting === GISSetting.DIRECT ? 'GIS Activation' : 'GIS Hosted'}
                    <br />
                    1.0
                  </span>
                  <Toggle
                    bsStyle="primary"
                    onToggle={this.onGISActivationToggleChange}
                    toggleState={agency.gisVersion === GISVersion.GIS_2_0}
                  >
                    <span className={classnames('gis-activation-section-label-after')}>
                      {agency.gisSetting === GISSetting.DIRECT ? 'GIS Activation' : 'GIS Hosted'}
                      <br />
                      2.0
                    </span>
                  </Toggle>
                </div>
              )}
              <DefaultCityModal
                show={this.state.isDefaultCityModalOpen}
                defaultCity={this.state.agency.defaultCity}
                onClose={this.toggleDefaultCityModal}
                onSubmit={this.onSubmitDefaultCity}
              />
              <GISDirectLinkModal
                show={this.state.isGISDirectLinkModalOpen}
                gisDirectURL={this.state.agency.gisDirectURL}
                onClose={this.toggleGISDirectLinkModal}
                onSubmit={this.onSubmitGISDirectLink}
              />
            </div>
            <div className={classnames('grid-4-cols-2-rows')}>
              <Input
                field={{
                  label: 'Group Permissions',
                  type: OtherFieldType.RADIOBUTTON,
                  isRequired: false,
                  value: this.state.agency.permissionPreference,
                }}
                width={12}
                valueLabelOptions={groupPermissions}
                name="permissionPreference"
                onChange={this.onGroupPermissionsChange}
                disabled={
                  this.props.agency ? this.props.agency.permissionPreference === PermissionPreference.GROUPS : true
                }
              />

              <Input
                field={{
                  label: 'Authentication',
                  type: OtherFieldType.RADIOBUTTON,
                  isRequired: false,
                  value: this.state.agency.SSOProvider,
                }}
                width={16}
                valueLabelOptions={ssoSettingsOptions}
                name="ssoProvider"
                onChange={this.onSSOProviderChange}
                disabled={this.props.agency && this.props.agency.SSOProvider === SSOProvider.AD}
              />
              <Input
                renderField={(name, onChange, value) => (
                  <div className="product-form__info__email-field">
                    <input name={name} onChange={onChange} type="text" placeholder="Enter Text" value={value} />
                    <span className="product-form__info__email-field__suffix">{AGENCY_EMAIL_SUFFIX}</span>
                  </div>
                )}
                field={{
                  label: 'Send From Email',
                  type: CustomFieldType.TEXT,
                  isRequired: true,
                  value: agency.sendFromEmail || '',
                }}
                error={error.sendFromEmail}
                width={12}
                name="sendFromEmail"
                onChange={this.handleChange}
              />
              <div className={classnames('product-form__info', 'crm', 'field-section', 'field-section--required')}>
                <Toggle
                  bsStyle="primary"
                  onToggle={this.onSendFromMessageToggle}
                  toggleState={this.state.agency.isSMSEnabled}
                  inverted
                >
                  <div className="field__label">
                    <label className="field__label-label" title="Send From Text Message">
                      Send From Text Message
                    </label>
                  </div>
                </Toggle>
                {(this.state.agency.isSMSEnabled || agency.crmIsActive) && (
                  <div className={classnames('number-field')}>
                    <Input
                      renderField={(name, onChange) => (
                        <div className="product-form__info__number-field">
                          <input
                            name={name}
                            onChange={onChange}
                            type="text"
                            value={this.state.agency.sendFromPhoneNumber}
                          />
                        </div>
                      )}
                      field={{
                        label: '',
                        type: CustomFieldType.NUMBER,
                        isRequired: true,
                        value: agency.sendFromPhoneNumber || '',
                      }}
                      error={error.sendFromPhoneNumber}
                      width={12}
                      name="sendFromPhoneNumber"
                      onChange={this.handleChange}
                    />
                  </div>
                )}
              </div>
            </div>

            <div>
              <div className={classnames('grid-3-cols-2-rows')}>
                <Input
                  field={{
                    label: 'Quicksight custom location field #1',
                    type: OtherFieldType.PICKLIST_WITH_HANDLER,
                    isRequired: false,
                    options: lcfOptions,
                    value: lcfIds[0],
                    showHandler: false,
                    multi: false,
                    labelType: LabelType.SELECTED_VALUE,
                  }}
                  multi={false}
                  placeholder="Select Field #1"
                  width={12}
                  name="lcfOne"
                  onChange={this.handleLcfChange(0)}
                />
              </div>
              <div className={classnames('grid-3-cols-2-rows')}>
                <Input
                  field={{
                    label: 'Quicksight custom location field #2',
                    type: OtherFieldType.PICKLIST_WITH_HANDLER,
                    isRequired: false,
                    options: lcfOptions,
                    value: lcfIds[1],
                    showHandler: false,
                    multi: false,
                    labelType: LabelType.SELECTED_VALUE,
                  }}
                  multi={false}
                  placeholder="Select Field #2"
                  width={12}
                  name="lcfTwo"
                  onChange={this.handleLcfChange(1)}
                />
              </div>
              <div className={classnames('grid-3-cols-2-rows')}>
                <Input
                  field={{
                    label: 'Quicksight custom location field #3',
                    type: OtherFieldType.PICKLIST_WITH_HANDLER,
                    isRequired: false,
                    options: lcfOptions,
                    value: lcfIds[2],
                    showHandler: false,
                    multi: false,
                    labelType: LabelType.SELECTED_VALUE,
                  }}
                  multi={false}
                  placeholder="Select Field #3"
                  width={12}
                  name="lcfThree"
                  onChange={this.handleLcfChange(2)}
                />
              </div>
            </div>
            <div className={classnames('grid-4-cols-2-rows')}>
              <Input
                field={{
                  label: 'Action Center',
                  type: OtherFieldType.RADIOBUTTON,
                  isRequired: false,
                  value: this.state.agency.hasActionCenterAccess,
                }}
                width={12}
                valueLabelOptions={actionCenterAccessOptions}
                name="hasActionCenterAccess"
                // onChange={this.onActionCenterAccessChange}
                disabled={true}
              />

              <Input
                field={{
                  label: 'FAQ Configuration',
                  type: OtherFieldType.RADIOBUTTON,
                  isRequired: false,
                  value: this.state.agency.isFaqEnabled,
                }}
                width={12}
                valueLabelOptions={faqOptions}
                name="isFaqEnabled"
                onChange={this.onFaqChange}
              />
              {agency.isFaqEnabled && (
                <div className={classnames('account-id')}>
                  {'Account ID '}
                  <span className={classnames('account-id-val')}>{agency.elevioAccountId}</span>
                  <span className={classnames('separator')}>|</span>
                  <a onClick={this.toggleFaqModal}>Change</a>
                </div>
              )}
              <FaqModal
                show={this.state.isFaqModalOpen}
                elevioAccountId={this.state.agency.elevioAccountId}
                onClose={this.toggleFaqModal}
                onSubmit={this.onSubmitFaq}
              />
              {!!roleOptions.length && (
                <Input
                  field={{
                    label: 'Default User Role',
                    type: OtherFieldType.PICKLIST_WITH_HANDLER,
                    isRequired: false,
                    showHandler: false,
                    value: roleId,
                    options: roleOptions,
                    multi: false,
                    labelType: LabelType.SELECTED_VALUE,
                  }}
                  multi={false}
                  width={12}
                  name="roleId"
                  onChange={this.onRoleChange}
                />
              )}
            </div>
          </div>
        </div>
        <div className="agency-setting__footer">
          <Button bsStyle={ButtonStyle.DEFAULT}>Cancel</Button>
          <Button bsStyle={ButtonStyle.PRIMARY} onClick={this.onSubmit}>
            Save Changes
          </Button>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: IReduxStore) => ({
  agency: state.agency.agencySettings,
  lcfs: state.locationCustomField.lcfs,
  roles: state.role.roles,
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchLcfs: (agencyId: number) => dispatch(fetchLcfs(agencyId, null, true)),
  fetchRoles: (agencyId: number) => dispatch(fetchRolesWithAgencyId(agencyId)),
  editAgencySettings: (agencyId: number, agencyRequest: IAgencySettingEditRequest) =>
    dispatch(editAgencySettings(agencyId, agencyRequest)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AgencySettings);
